import { Component, Input, OnInit, OnChanges, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbService } from '@k12/navigation';
import { Subject, takeUntil } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-user-record-create-task',
  imports: [MatButtonModule, MatIconModule, MatProgressBarModule],
  templateUrl: './user-record-create-task.component.html',
  styleUrls: ['./user-record-create-task.component.scss']
})
export class UserRecordCreateTaskComponent implements OnInit, OnChanges, OnDestroy {
  // Input for services that need to be injected from the consuming application
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() userRecordService: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() programPartnerService: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() taskFormComponent: any;

  @Output() formSubmit = new EventEmitter<boolean>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  personaId: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userId: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userData: any = null;
  accountId: string | null = null;
  programPartnerTypeId: number | null = null;
  private routeUnsubscribe = new Subject<void>();

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    private router: Router,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    private route: ActivatedRoute,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    // Get userId from route parameters
    this.route.params.pipe(takeUntil(this.routeUnsubscribe)).subscribe((params) => {
      this.userId = params['id'] || null;
      if (this.userId) {
        this.getUserDataForBreadcrumbs();
      }
    });

    if (history.state && history.state['personaId']) {
      this.personaId = history.state['personaId'];
    }

    if (history.state && history.state['providerId']) {
      this.userId = history.state['providerId'];
      this.getUserDataForBreadcrumbs();
    }

    document.documentElement.scrollTop = 0;
  }

  ngOnDestroy(): void {
    this.routeUnsubscribe.next();
    this.routeUnsubscribe.complete();
    this.breadcrumbService.clearDynamicBreadcrumbs();
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnChanges(): void {}

  // Method to get user data and set breadcrumbs
  private getUserDataForBreadcrumbs() {
    if (this.userId && this.programPartnerService) {
      // The userId from route is the programPartnerId (the partner's id)
      // We need to look up the partner to get both programPartnerTypeId and accountId
      this.programPartnerService.getAllProgramPartners().subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (response: any) => {
          if (response.body && response.body.length > 0) {
            // Find the partner by id (programPartnerId)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const partner = response.body.find((p: any) => p.id === this.userId);

            if (partner) {
              // Get the correct programPartnerTypeId and accountId from the partner
              this.programPartnerTypeId = partner.programPartnerTypeId;
              this.accountId = partner.accountId || this.userId; // Fallback to userId if accountId not available
            } else {
              // If partner not found, use userId as accountId and try to get type from user data
              this.accountId = this.userId;
            }
          } else {
            this.accountId = this.userId;
          }

          // Get user details for breadcrumbs
          if (this.userRecordService) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.userRecordService.getProviderDetails(this.userId).subscribe((res: any) => {
              this.userData = res;
              this.setBreadcrumbsForCreateTask();
            });
          }
        },

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        error: (err: any) => {
          // On error, fallback to using userId as accountId
          this.accountId = this.userId;
          if (this.userRecordService) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.userRecordService.getProviderDetails(this.userId).subscribe((res: any) => {
              this.userData = res;
              this.setBreadcrumbsForCreateTask();
            });
          }
        }
      });
    } else if (this.userId && this.userRecordService) {
      this.accountId = this.userId;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.userRecordService.getProviderDetails(this.userId).subscribe((res: any) => {
        this.userData = res;
        this.setBreadcrumbsForCreateTask();
      });
    }
  }

  // Method to set breadcrumbs for the create task page
  private setBreadcrumbsForCreateTask() {
    if (this.userData && this.userData.firstName && this.userData.lastName) {
      const userName = `${this.userData.firstName} ${this.userData.lastName}`;
      // Set breadcrumbs for user record, edit, and create task pages
      this.breadcrumbService.setNestedDynamicBreadcrumbs(
        '/work-queue',
        this.userId,
        userName,
        undefined,
        'Create a New Task'
      );
    }
  }

  submitForm(success: boolean): void {
    if (success) {
      this.router.navigate(['/work-queue', this.userId]);
    }
    this.formSubmit.emit(success);
  }
}

